import React from 'react';
import { Phone, Mail, MapPin, Heart } from 'lucide-react';
import { companyInfo } from '../utils/mockData';
import { useLanguage } from '../contexts/LanguageContext';

const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    Services: {
      titleEn: 'Services',
      titleZh: '服务',
      links: [
        { en: 'My Aged Care Concierge', zh: '养老护理申请协助' },
        { en: 'Personal Care', zh: '个人护理' },
        { en: 'Domestic Assistance', zh: '家务协助' },
        { en: 'Nursing Care', zh: '护理服务' },
        { en: 'Companionship', zh: '陪伴服务' },
        { en: 'Transport', zh: '交通协助' }
      ]
    },
    Programs: {
      titleEn: 'Programs',
      titleZh: '项目',
      links: [
        { en: 'Support at Home', zh: '居家护理' },
        { en: 'Home Care Packages', zh: '居家护理包' },
        { en: 'Funding Levels', zh: '资金等级' },
        { en: 'AT-HM Scheme', zh: 'AT-HM 计划' }
      ]
    },
    Company: {
      titleEn: 'Company',
      titleZh: '公司',
      links: [
        { en: 'About Us', zh: '关于我们' },
        { en: 'Our Team', zh: '我们的团队' },
        { en: 'Careers', zh: '职业机会' },
        { en: 'Contact', zh: '联系我们' }
      ]
    }
  };

  return (
    <footer className="bg-navy-900 text-white pt-20 pb-8" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Top: CTA banner */}
        <div className="mb-16 p-8 lg:p-12 bg-gradient-to-br from-eucalyptus-600 to-eucalyptus-700 rounded-3xl">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-3">
                {t('Care starts with a conversation', '护理从一次对话开始')}
              </h3>
              <p className="text-eucalyptus-50 max-w-lg">
                {t(
                  'Speak with a bilingual coordinator today. Free assessment, no commitment.',
                  '今天就与双语协调员聊聊。免费评估，无须承诺。'
                )}
              </p>
            </div>
            <a
              href="#contact"
              className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-4 bg-white text-navy-900 rounded-full font-semibold transition-all hover:shadow-2xl hover:scale-105"
              data-testid="footer-cta"
            >
              {t('Book Free Consultation', '预约免费咨询')}
            </a>
          </div>
        </div>

        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-16 mb-16">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <div className="mb-6 bg-white/5 backdrop-blur-sm rounded-2xl p-4 inline-block">
              <img src={companyInfo.logo} alt="AU YH Care - Australian Home Care" className="h-16 w-auto" />
            </div>
            <p className="text-gray-400 leading-relaxed mb-6 max-w-md">
              {t(
                'Bilingual home care and Support at Home services for Chinese families across Greater Sydney. Delivered with warmth, dignity and cultural understanding.',
                '为大悉尼地区华人家庭提供双语居家护理与居家养老服务。以温暖、尊严和文化理解提供服务。'
              )}
            </p>
            <div className="space-y-3">
              <a href={`tel:${companyInfo.phoneRaw}`} className="flex items-center gap-3 text-gray-300 hover:text-eucalyptus-300 transition-colors">
                <Phone className="w-4 h-4" />
                {companyInfo.phone}
              </a>
              <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-3 text-gray-300 hover:text-eucalyptus-300 transition-colors">
                <Mail className="w-4 h-4" />
                {companyInfo.email}
              </a>
              <div className="flex items-start gap-3 text-gray-300 pt-3 mt-3 border-t border-navy-800/50" data-testid="footer-wechat">
                <img
                  src={companyInfo.wechatQR}
                  alt={`WeChat QR for ${companyInfo.wechat}`}
                  className="w-16 h-16 rounded-lg object-cover bg-white p-1 flex-shrink-0"
                  loading="lazy"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="currentColor" aria-hidden="true">
                      <path d="M8.691 2C4.768 2 1.5 4.68 1.5 8.14c0 1.9 1.15 3.68 2.99 4.86l-.61 1.86 2.24-1.12c.62.09 1.24.14 1.87.14.16 0 .32-.01.48-.02-.09-.34-.14-.7-.14-1.06 0-3.16 2.99-5.72 6.68-5.72.11 0 .22 0 .33.01C14.94 4 12.05 2 8.69 2zm3.53 4.98a.8.8 0 1 1 0-1.6.8.8 0 0 1 0 1.6zm-6.42 0a.8.8 0 1 1 0-1.6.8.8 0 0 1 0 1.6zm9.51 1.51c-3.2 0-5.79 2.16-5.79 4.83s2.59 4.83 5.79 4.83c.65 0 1.27-.09 1.86-.24l1.7.94-.47-1.52c1.42-1.06 2.34-2.66 2.34-4.51 0-2.67-2.59-4.83-5.79-4.83zm-1.92 4.03a.7.7 0 1 1 0-1.4.7.7 0 0 1 0 1.4zm3.86 0a.7.7 0 1 1 0-1.4.7.7 0 0 1 0 1.4z"/>
                    </svg>
                    <span className="text-xs uppercase tracking-wider text-gray-400">WeChat · 微信</span>
                  </div>
                  <div className="font-mono font-semibold text-white text-sm mt-1 break-all">{companyInfo.wechat}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <MapPin className="w-4 h-4" />
                {companyInfo.location}
              </div>
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([key, section]) => (
            <div key={key}>
              <h4 className="font-bold text-white mb-5">
                {language === 'zh' ? section.titleZh : section.titleEn}
              </h4>
              <ul className="space-y-3">
                {section.links.map((link, i) => (
                  <li key={i}>
                    <a
                      href="#"
                      className="text-gray-400 hover:text-eucalyptus-300 transition-colors text-sm"
                    >
                      {language === 'zh' ? link.zh : link.en}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Associated provider notice */}
        <div className="p-6 bg-navy-800/50 border border-navy-700 rounded-2xl mb-8" data-testid="associated-provider-notice">
          <div className="flex items-start gap-3">
            <Heart className="w-5 h-5 text-eucalyptus-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-300 leading-relaxed">
              <span className="font-semibold text-white">{companyInfo.name}</span> (ABN: {companyInfo.abn}) {t('operates as an associated aged care provider, delivering Support at Home and Home Care Package services in partnership with a registered provider.', '作为关联养老服务商运营，与注册服务商合作提供居家养老与居家护理服务。')}
            </p>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-navy-800 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
          <p>© {currentYear} {companyInfo.name}. {t('All rights reserved.', '版权所有。')}</p>
          <div className="flex gap-6">
            <a href="#" className="hover:text-eucalyptus-300 transition-colors">
              {t('Privacy Policy', '隐私政策')}
            </a>
            <a href="#" className="hover:text-eucalyptus-300 transition-colors">
              {t('Terms of Service', '服务条款')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
